import { ListItem, ListItemText } from "@mui/material";
import React from "react";

import {
  StyledContentList,
  StyledContentListItem,
  StyledContentParagraph,
  StyledContentPageTitle,
} from "src/components";
import ContentPageWrapper from "src/containers/ContentPageWrapper";

import ActionPlanTable from "./_components/ActionPlanTable";

const MultiAnnualPlan = () => {
  const title = "Plan pluriannuel d'accessibilité 2025-2027";

  return (
    <ContentPageWrapper title={title}>
      <StyledContentPageTitle variant="h4">Introduction</StyledContentPageTitle>
      <StyledContentParagraph>
        L'article 47 de la loi n°2005-102 du 11 février 2005 pour l'égalité des droits et des
        chances, la participation et la citoyenneté des personnes rend obligatoire à tout service de
        communication publique en ligne d'être accessible à tous et de publier un schéma pluriannuel
        de mise en accessibilité, d’une durée maximale de trois ans, présentant la politique de
        l’entité concernée en matière d’accessibilité numérique.
      </StyledContentParagraph>
      <StyledContentParagraph>
        Emeria Technologies a publié un nouveau schéma, au titre des années 2024 à 2026, qui est
        susceptible d’évoluer pour refléter au mieux ses engagements.
      </StyledContentParagraph>
      <StyledContentParagraph>
        Le présent plan pluriannuel d’accessibilité numérique s’inscrit dans une démarche
        d’amélioration continue. Il fait l’objet d’une <strong>mise à jour régulière</strong>,
        notamment à travers un
        {/* eslint-disable-next-line no-restricted-syntax */}
        <strong> plan d’actions annuel</strong>, afin de tenir compte de l’avancement des actions
        engagées, des résultats des audits réalisés et des évolutions du service MyFoncia.
      </StyledContentParagraph>
      <StyledContentParagraph>
        La version actuelle intègre les actions menées en <strong>2025</strong> ainsi que le{" "}
        <strong>plan d’actions prévu pour l’année 2026</strong>.
      </StyledContentParagraph>
      <StyledContentPageTitle variant="h4">
        Définition de l'accessibilité numérique
      </StyledContentPageTitle>
      <StyledContentParagraph>
        L'accessibilité numérique permet d'accéder aux contenus (sites web, documents bureautiques,
        supports multimédias, intranets d'entreprise, applications mobiles…), quelle que soit sa
        façon de naviguer.
      </StyledContentParagraph>
      <StyledContentParagraph>
        Elle est indispensable aux personnes en situation de handicap pour :
        <StyledContentList disablePadding>
          <StyledContentListItem disablePadding>
            <ListItemText>S'informer </ListItemText>
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText>Communiquer</ListItemText>
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText>Accomplir des démarches administratives</ListItemText>
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText>Mener une activité professionnelle...</ListItemText>
          </StyledContentListItem>
        </StyledContentList>
      </StyledContentParagraph>
      <StyledContentParagraph>
        L'accessibilité numérique profite à tous : aux personnes âgées, aux personnes en situation
        de handicap temporaire, aux personnes peu à l'aise avec Internet…
      </StyledContentParagraph>
      <StyledContentParagraph>
        Elle s'inscrit dans une démarche d'égalité et constitue un enjeu politique et social
        fondamental afin de garantir à tous, sans discrimination, le même accès à l'information et
        aux services en ligne.
      </StyledContentParagraph>
      <StyledContentParagraph>
        Il s’agit d’un domaine transverse qui concerne toutes les personnes impliquées dans la
        création, la maintenance et l'utilisation des dispositifs numériques : décideurs, chefs de
        projet, designers, développeurs, producteurs de contenus.
      </StyledContentParagraph>
      <StyledContentPageTitle variant="h4">Politique d'accessibilité</StyledContentPageTitle>
      <StyledContentParagraph>
        L'accessibilité numérique de <strong>l’espace client</strong> est au cœur des préoccupations
        liées au développement ou à la mise à disposition de sites web ou d'applications auprès du
        public.
      </StyledContentParagraph>
      <StyledContentParagraph>
        L'accessibilité numérique nécessite de faire le lien entre le handicap et les difficultés ou
        les limitations potentielles de la personne à l’utilisation d'un outil informatique et les
        solutions numériques qui existent et qui ont été déployées au sein de Emeria Technologies.
      </StyledContentParagraph>
      <StyledContentParagraph>
        Face à une pluralité de situations de handicap, le service des systèmes d'information et le
        référent accessibilité numérique fait le lien entre le handicap et l'informatique pour
        trouver une adaptation qui conviennent à tous les acteurs.
      </StyledContentParagraph>
      <StyledContentParagraph>
        L’élaboration, le suivi et la mise à jour de ce schéma pluriannuel sont placés sous la
        responsabilité d'Emeria Technologies.{" "}
      </StyledContentParagraph>
      <StyledContentParagraph>
        En lien avec le service des systèmes d’information, la mission de cette équipe est de :
        <StyledContentList disablePadding>
          <StyledContentListItem disablePadding>
            <ListItemText>
              Promouvoir l'accessibilité par la diffusion des normes et des bonnes pratiques
            </ListItemText>
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText>
              Accompagner les équipes par des actions de sensibilisation et formation notamment ou
              de support lors de la conception et le développement des projets numériques
            </ListItemText>
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText>
              Veiller à l'application de la loi n° 2005-102 du 11 février 2005
            </ListItemText>
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText>
              Être en support des projets qui assurent la prise en charge des demandes des
              utilisateurs et de manière générale la qualité du service rendu aux utilisateurs en
              situation de handicap
            </ListItemText>
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText>Effectuer une veille sur les évolutions réglementaires.</ListItemText>
          </StyledContentListItem>
        </StyledContentList>
      </StyledContentParagraph>
      <StyledContentPageTitle variant="h4">Plan annuel</StyledContentPageTitle>
      <StyledContentParagraph>
        Le programme pluriannuel d’accessibilité d'Emeria Technologies repose sur plusieurs piliers
        : la formation et sensibilisation des équipes, la réalisation d’audits, l’analyse des
        retours utilisateurs et utilisatrices, la mise en œuvre de mesures techniques correctives,
        ainsi que l’intégration systématique de l’accessibilité dans les processus de l’entreprise.
      </StyledContentParagraph>
      <StyledContentList>
        <ListItem>
          <ListItemText>
            <strong>Mesure régulière la conformité au RGAA </strong>
          </ListItemText>
        </ListItem>
      </StyledContentList>
      <StyledContentParagraph sx={{ marginTop: "0 !important" }}>
        Emeria Technologies s’engage à évaluer régulièrement la conformité de ses services
        numériques au Référentiel Général d’Amélioration de l’Accessibilité (RGAA). Des audits
        d’accessibilité, mêlant tests automatisés et vérifications manuelles, sont réalisés
        périodiquement sur l’ensemble des sites et applications, en se basant sur la version en
        vigueur du RGAA. Les résultats sont documentés, priorisés et intégrés dans les cycles
        d’amélioration continue, afin d’assurer une mise en conformité durable et progressive.{" "}
      </StyledContentParagraph>
      <StyledContentList>
        <ListItem>
          <ListItemText>
            <strong>Formation et sensibilisation </strong>
          </ListItemText>
        </ListItem>
      </StyledContentList>
      <StyledContentParagraph sx={{ marginTop: "0 !important" }}>
        Former et sensibiliser nos équipes est essentiel pour faire progresser l’accessibilité.
        Emeria Technologies propose des formations et modules de sensibilisation aux collaborateurs
        couvrant des thématiques telles que la sensibilisation au handicap, les bonnes pratiques
        inclusives et la valorisation des personnes en situation de handicap. Des modules de
        e-learning sont déjà disponibles en interne et des webinaires sont organisés régulièrement.{" "}
      </StyledContentParagraph>
      <StyledContentParagraph>
        Le thème de l’accessibilité numérique, ainsi que les bonnes pratiques à adopter, notamment
        en matière de développement d’outils numériques, sera présenté à l’ensemble de l’entreprise
        lors d’une réunion interne organisée deux fois par mois.{" "}
      </StyledContentParagraph>

      <StyledContentList>
        <ListItem>
          <ListItemText>
            <strong>Audits et retours d’expérience </strong>
          </ListItemText>
        </ListItem>
      </StyledContentList>
      <StyledContentParagraph sx={{ marginTop: "0 !important" }}>
        Emeria Technologies combine audits automatisés et tests manuels menés avec des personnes en
        situation de handicap. Régulièrement, des audits sont effectués sur les technologies
        accessibles au public, conformément aux critères WCAG 2.1 AA. Ces tests mobilisent divers
        outils d’assistance : lecteurs d’écran, claviers, agrandisseurs d’écran, etc.{" "}
      </StyledContentParagraph>
      <StyledContentParagraph>
        Les non-conformités sont enregistrées dans notre outil de suivi Jira, où elles sont
        priorisées et traités par les équipes produit et développement. En complément, l’outil
        Google Lighthouse est intégré dans notre chaîne de développement pour un suivi continu{" "}
      </StyledContentParagraph>

      <StyledContentList>
        <ListItem>
          <ListItemText>
            <strong>Actions correctives techniques </strong>
          </ListItemText>
        </ListItem>
      </StyledContentList>
      <StyledContentParagraph sx={{ marginTop: "0 !important" }}>
        Les audits et les retours utilisateurs permettent d’identifier et de traiter en priorité les
        problèmes les plus impactants, toujours selon les normes WCAG 2.1 AA. Chaque problème fait
        l’objet d’une classification technique et d’une hiérarchisation rigoureuse. Les corrections
        se concentrent principalement sur :
        <StyledContentList disablePadding>
          <StyledContentListItem disablePadding>
            <ListItemText>Révision de la charte graphique</ListItemText>
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText>Révision du code de source du site </ListItemText>
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText>Contrôle de saisie des formulaires </ListItemText>
          </StyledContentListItem>
        </StyledContentList>
      </StyledContentParagraph>

      <StyledContentList>
        <ListItem>
          <ListItemText>
            <strong>Intégration dans les processus internes </strong>
          </ListItemText>
        </ListItem>
      </StyledContentList>
      <StyledContentParagraph sx={{ marginTop: "0 !important" }}>
        Au-delà des mesures correctives, Emeria Technologies s’engage à intégrer durablement
        l’accessibilité dans ses processus métiers. Nos équipes produit, design et développement
        appliquent les normes WCAG dès la conception des nouvelles fonctionnalités, pour garantir un
        code accessible dès sa publication.
      </StyledContentParagraph>
      <StyledContentParagraph>
        Un guide de bonnes pratiques de développement a été élaboré et mis à disposition des équipes
        techniques afin de garantir que les évolutions et nouvelles fonctionnalités soient conçues
        dès l’amont dans le respect des critères d’accessibilité définis par le RGAA. Ce document
        couvre les principes fondamentaux du développement accessible (structure sémantique,
        navigation clavier, gestion des contrastes, compatibilité avec les technologies
        d’assistance, etc.) et constitue une référence commune pour l’ensemble des développeur·se·s.{" "}
      </StyledContentParagraph>
      <StyledContentPageTitle variant="h4">Plan pluriannuel (2025-2027)</StyledContentPageTitle>
      <ActionPlanTable />
    </ContentPageWrapper>
  );
};

export default MultiAnnualPlan;
